"use client";

import useIsMobile from "@/hooks/use-is-mobile";
import { Dialog } from "@base-ui/react/dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ScrollArea } from "./scroll-area";
import { Icon } from "@/components/ui/icon";

const desktopVariants = cva(
  "fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col overflow-hidden border-l border-border bg-surface-header outline-none transition-transform duration-200 ease-out data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full",
  {
    variants: {
      size: {
        sm: "max-w-[30rem]",
        md: "max-w-[35rem]",
        lg: "max-w-[32rem]",
        xl: "max-w-[44rem]",
        editor: "max-w-[68rem]",
        profile: "max-w-[69rem]",
      },
    },
    defaultVariants: {
      size: "xl",
    },
  },
);

const mobileClasses =
  "fixed inset-x-0 bottom-0 z-50 flex h-[92dvh] w-full flex-col overflow-hidden rounded-t-xl border-t border-border bg-surface-header outline-none transition-transform duration-200 ease-out data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full";

const backdropClasses =
  "fixed inset-0 z-50 bg-black/60 transition-opacity duration-150 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0";

export interface SheetProps extends VariantProps<typeof desktopVariants> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** When true, Base UI's modal lock/focus trap is disabled so portaled dropdowns inside the sheet can receive focus. */
  disableFocusTrap?: boolean;
  /**
   * When true, clicks outside the sheet do NOT dismiss it. Use to suppress
   * spurious outside-press closes from the same click that opened the sheet
   * — the click event continues propagating after React mounts the dialog,
   * lands on the freshly-rendered backdrop, and base-ui interprets it as a
   * dismissal. Toggle this on for ~300ms after open to avoid the flicker.
   */
  disablePointerDismissal?: boolean;
  /**
   * Element focused when the sheet opens, forwarded to Base UI's popup (same
   * escape hatch `ModalContent` exposes). Use when the sheet leads with a
   * heading that should announce itself instead of the first focusable child.
   */
  initialFocus?: React.ComponentProps<typeof Dialog.Popup>["initialFocus"];
}

export function Sheet({
  open,
  onOpenChange,
  size,
  children,
  className,
  style,
  disableFocusTrap = false,
  disablePointerDismissal = false,
  initialFocus,
}: SheetProps) {
  const isMobile = useIsMobile();

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={!disableFocusTrap}
      disablePointerDismissal={disablePointerDismissal}
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className={backdropClasses}
          onClick={
            disablePointerDismissal ? () => onOpenChange(false) : undefined
          }
        />
        <Dialog.Popup
          className={cn(
            isMobile ? mobileClasses : desktopVariants({ size }),
            className,
          )}
          style={style}
          initialFocus={initialFocus}
          aria-describedby={undefined}
        >
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface SheetHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function SheetHeader({ children, className }: SheetHeaderProps) {
  return (
    <div
      className={cn(
        "flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface-header px-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface SheetTitleProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Render the title as a real heading. Defaults to a `span` so existing
   * sheets keep their flat outline; pass `"h2"` when the sheet body is a
   * document section that needs a heading in the a11y tree.
   */
  as?: "span" | "h2";
  titleRef?: React.Ref<HTMLElement>;
  tabIndex?: number;
}

export function SheetTitle({
  children,
  className,
  as: As = "span",
  titleRef,
  tabIndex,
}: SheetTitleProps) {
  return (
    <Dialog.Title
      render={
        <As
          ref={titleRef as React.Ref<HTMLHeadingElement & HTMLSpanElement>}
          tabIndex={tabIndex}
          className={cn(
            "text-lg font-medium text-heading outline-none",
            className,
          )}
        >
          {children}
        </As>
      }
    />
  );
}

export interface SheetCloseProps {
  "aria-label"?: string;
  className?: string;
}

export function SheetClose({
  "aria-label": ariaLabel = "Close",
  className,
}: SheetCloseProps) {
  return (
    <Dialog.Close
      render={
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            "flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70",
            className,
          )}
        >
          <Icon name="IconCrossMedium" size={16} />
        </button>
      }
    />
  );
}

export interface SheetBodyProps {
  children: React.ReactNode;
  /**
   * Classes applied to the inner content wrapper. Use for layout overrides
   * like `gap-*`, extra padding, or a custom `flex` direction.
   */
  className?: string;
  /**
   * Escape hatch for styling the outer ScrollArea root (e.g. background).
   * Avoid adding `flex`/`flex-col`/`gap-*` here — those belong to
   * `className` because the root also hosts absolutely-positioned
   * scrollbars that a flex layout would disturb.
   */
  rootClassName?: string;
  /**
   * Classes on ScrollArea content (the sized box inside the viewport).
   * Use `min-h-full` when centering empty states so percentage heights
   * resolve against the sheet body, not only the short empty children.
   */
  contentClassName?: string;
}

export function SheetBody({
  children,
  className,
  rootClassName,
  contentClassName,
}: SheetBodyProps) {
  return (
    <ScrollArea
      constrainContentWidth
      className={cn(
        "flex-1 min-h-0 bg-background dark:bg-[#18181A]",
        rootClassName,
      )}
      contentClassName={contentClassName}
    >
      <div
        className={cn(
          "px-4 py-4 flex flex-col gap-page sm:px-6 sm:py-6",
          className,
        )}
      >
        {children}
      </div>
    </ScrollArea>
  );
}

export interface SheetFooterProps {
  children?: React.ReactNode;
  className?: string;
}

export function SheetFooter({ children, className }: SheetFooterProps) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-end gap-3 border-t border-border bg-surface-header px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4",
        className,
      )}
    >
      {children}
    </div>
  );
}
